// src/components/post/CommentsSection.jsx
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  HiChatBubbleLeft,
  HiPaperAirplane,
  HiPencil,
  HiTrash
} from 'react-icons/hi2'
import { useAuth } from '../../hooks/useAuth'
import toast from 'react-hot-toast'

export default function CommentsSection({
  postId,
  onAddComment,
  onDeleteComment,       // optional
  onFetchInterpretationComments, // optional
  user
}) {
  const {
    isModerator,
    canModifyComment,
    getAuthenticatedApi
  } = useAuth()

  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const [replyContent, setReplyContent] = useState('')
  const [editingComment, setEditingComment] = useState(null)
  const [editContent, setEditContent] = useState('')
  const [paginatedComments, setPaginatedComments] = useState([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const loadMoreComments = async (reset = false) => {
    if (loadingMore) return
    setLoadingMore(true)

    // if reset, start from scratch
    const nextPage = reset ? 1 : page + 1
    if (reset) {
      setPaginatedComments([])
      setPage(0)
      setHasMore(true)
    }

    try {
      const api = getAuthenticatedApi()
      const res = await api.get(
        `/comments/post/${postId}?page=${nextPage}&page_size=5&include_nested=true`
      )
      const newComments = res.data.comments

      setPaginatedComments(prev =>
        reset ? newComments : [...prev, ...newComments]
      )
      setPage(nextPage)
      if (newComments.length < 5) setHasMore(false)
    } catch (err) {
      toast.error('Failed to load comments')
    } finally {
      setLoadingMore(false)
    }
  }

  // Initial load & whenever postId changes
  useEffect(() => {
    loadMoreComments(true)
  }, [postId])

  // -- handlers --

  const handleSubmitComment = async e => {
    e.preventDefault()
    if (!newComment.trim() || submitting) return

    setSubmitting(true)
    try {
      await onAddComment(newComment.trim())
      setNewComment('')
      // **reload from page 1**
      await loadMoreComments(true)
    } catch (err) {
      console.error(err)
      toast.error('Failed to post comment')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmitReply = async (e, parentId) => {
    e.preventDefault()
    if (!replyContent.trim() || submitting) return

    setSubmitting(true)
    try {
      await onAddComment(replyContent.trim(), parentId)
      setReplyContent('')
      setReplyingTo(null)
      // **reload from page 1**
      await loadMoreComments(true)
    } catch (err) {
      console.error(err)
      toast.error('Failed to post reply')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = async id => {
    if (!editContent.trim()) return
    try {
      const api = getAuthenticatedApi()
      await api.put(`/comments/${id}`, { content: editContent.trim() })
      toast.success('Comment updated')
      setEditingComment(null)
      setEditContent('')
      // **reload from page 1**
      await loadMoreComments(true)
    } catch (err) {
      toast.error('Failed to update comment')
    }
  }

  const handleDelete = async id => {
    if (!window.confirm('Delete this comment?')) return
    try {
      const api = getAuthenticatedApi()
      await api.delete(`/comments/${id}`)
      toast.success('Comment deleted')
      // **reload from page 1**
      await loadMoreComments(true)
      // also bubble up if parent wants to do something
      onDeleteComment?.(id)
    } catch (err) {
      toast.error('Failed to delete comment')
    }
  }

  // -- rendering --

  const renderComment = (comment, depth = 0) => {
    const canModify = canModifyComment(comment)
    const isOwner = comment.user_id === user?.user_id
    const showReplyForm = replyingTo === comment.id

    return (
      <motion.div
        key={comment.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border-l-2 border-gray-700 pl-4 mb-4 ${
          depth > 0 ? 'ml-4' : ''
        }`}
      >
        <div className="bg-dark-gray/40 p-4 rounded-xl">
          <div className="flex items-center mb-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center font-bold text-sm">
              {comment.user?.name?.[0]?.toUpperCase() || 'A'}
            </div>
            <span className="ml-2 font-medium">{comment.user?.name}</span>
            <span className="ml-auto text-xs text-gray-400">
              {new Date(comment.created_at).toLocaleDateString()}
            </span>
          </div>

          {editingComment === comment.id ? (
            <>
              <textarea
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                rows={2}
                className="w-full bg-gray-700 p-2 rounded"
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => handleEdit(comment.id)}
                  className="px-3 py-1 bg-green-600 rounded disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingComment(null)}
                  className="px-3 py-1 bg-gray-600 rounded"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <p className="text-gray-300 mb-2">{comment.content}</p>
          )}

          <div className="flex gap-4 text-sm text-gray-400">
            <button
              onClick={() =>
                setReplyingTo(
                  replyingTo === comment.id ? null : comment.id
                )
              }
              className="flex items-center gap-1"
            >
              <HiChatBubbleLeft className="w-4 h-4" /> Reply
            </button>

            {isOwner && (
              <button
                onClick={() => {
                  setEditingComment(comment.id)
                  setEditContent(comment.content)
                }}
                className="flex items-center gap-1"
              >
                <HiPencil className="w-4 h-4" /> Edit
              </button>
            )}

            {canModify && (
              <button
                onClick={() => handleDelete(comment.id)}
                className="flex items-center gap-1"
              >
                <HiTrash className="w-4 h-4" /> Delete
              </button>
            )}
          </div>

          {showReplyForm && (
            <form
              onSubmit={e => handleSubmitReply(e, comment.id)}
              className="mt-2"
            >
              <textarea
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                rows={2}
                className="w-full bg-gray-700 p-2 rounded"
                placeholder="Write a reply..."
              />
              <button
                type="submit"
                disabled={!replyContent.trim() || submitting}
                className="mt-1 px-4 py-1 bg-primary rounded disabled:opacity-50"
              >
                <HiPaperAirplane className="inline-block w-4 h-4 mr-1" />
                Reply
              </button>
            </form>
          )}
        </div>

        {/* nested replies */}
        {comment.replies?.length > 0 &&
          comment.replies.map(reply =>
            renderComment(reply, depth + 1)
          )}
      </motion.div>
    )
  }

  const topLevel = paginatedComments.filter(
    c => !c.interpretation_id && !c.parent_id
  )

  return (
    <div>
      {user ? (
        <form onSubmit={handleSubmitComment} className="mb-6">
          <textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            rows={3}
            className="w-full bg-gray-700 p-2 rounded"
            placeholder="Share your thoughts..."
          />
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="mt-2 px-4 py-1 bg-primary rounded disabled:opacity-50"
          >
            <HiPaperAirplane className="inline-block w-4 h-4 mr-1" />
            {submitting ? 'Posting...' : 'Post Comment'}
          </button>
        </form>
      ) : (
        <p className="text-gray-400 mb-6">
          Please log in to join the discussion.
        </p>
      )}

      {topLevel.length > 0 ? (
        topLevel.map(comment => renderComment(comment))
      ) : (
        <p className="text-gray-400">No comments yet. Be the first!</p>
      )}

      {hasMore && (
        <div className="text-center mt-4">
          <button
            onClick={() => loadMoreComments(false)}
            disabled={loadingMore}
            className="px-4 py-1 bg-primary rounded disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load More Comments'}
          </button>
        </div>
      )}
    </div>
  );
};

